import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
} from "@angular/core";
import { ActivatedRoute, ParamMap, RouterLink, convertToParamMap } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { select, Store } from "@ngrx/store";
import { NgxSafePipesModule } from "@it-era/ngx-safe-pipes";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";
import { ToastrService } from "@iqx-limited/ngx-toastr";

import { CatalogueSelectors } from "src/app/core/store/selectors";
import { Seo } from "src/app/core/services/seo.service";
import { CoreActions } from "src/app/core/store/actions";
import { environment } from "src/environments/environment";
import { PricePipe } from "src/app/core/pipes/price.pipe";
import { Product } from "../../models/product.model";
import { Document } from "../../models/document.model";
import { GalleryComponent } from "../gallery/gallery.component";
import { toSignal } from "@angular/core/rxjs-interop";

@Component({
  selector: "audio-product",
  templateUrl: "./product.component.html",
  styleUrls: ["./product.component.sass"],
  imports: [
    RouterLink,
    FaIconComponent,
    GalleryComponent,
    NgxSafePipesModule,
    PricePipe,
  ],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductComponent {
  private sanitizer = inject(DomSanitizer);
  private route = inject(ActivatedRoute);
  private toastr = inject(ToastrService);
  private store = inject(Store);
  private seo = inject(Seo);

  environment = environment;
  product$!: Observable<Product>;
  slides$!: Observable<Document[]>;
  images$!: Observable<Document[]>;
  pdfs$!: Observable<Document[]>;

  public $mode = signal<"ttc" | "ht">("ttc");

  $products: Signal<Product[]> = toSignal(
    this.store
      .pipe(select(CatalogueSelectors.getProducts))
      .pipe(filter((products) => products && !!products.length)),
    { initialValue: [] },
  );

  $paramMap: Signal<ParamMap> = toSignal(this.route.paramMap, { initialValue: convertToParamMap({}) });

  $product: Signal<
    Product & {
      real_price: number;
    }
  > = computed(() => {
    const product = this.$products().filter(
      (product) =>
        product.slug === this.$paramMap().get("product")?.replace("—", "--"),
    )[0];

    return {
      ...product,
      real_price: product.items?.length
        ? product.items?.reduce((acc, item) => acc + item.price * (item.qty ?? 0), 0)
        : product.price,
      items: [...product.items].sort((a: Product, b: Product) =>
        +a.price < +b.price ? 1 : +a.price === +b.price ? 0 : -1,
      ),
      variants: [...product.variants].sort((a: Product, b: Product) =>
        +a.price < +b.price ? -1 : +a.price === +b.price ? 0 : 1,
      ),
    };
  });

  $slides: Signal<Document[]> = computed(() =>
    [...this.$product().documents]
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .filter((document) => {
        switch (document["content-type"]) {
          case "image/png":
          case "image/jpeg":
          case "image/webp":
          case "video/mp4":
          case "video/webm":
            return true;
          default:
            return false;
        }
      }),
  );

  $images: Signal<Document[]> = computed(() =>
    this.$slides().filter((slide) => {
      switch (slide["content-type"]) {
        case "image/png":
        case "image/jpeg":
        case "image/webp":
          return true;
        default:
          return false;
      }
    }),
  );

  $pdfs: Signal<Document[]> = computed(() =>
    [...this.$product().documents]
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .filter((document) => {
        switch (document["content-type"]) {
          case "application/pdf":
            return true;
          default:
            return false;
        }
      }),
  );

  $image: Signal<SafeResourceUrl> = computed(() =>
    this.sanitizer.bypassSecurityTrustResourceUrl(
      environment.apiUrl + this.$images()?.[0]?.src + ".webp",
    ),
  );

  constructor() {
    effect(() => {
      if (!this.$products() || !this.$products().length) {
        this.toastr.error(
          "Le produit n'existe pas ou plus." /*, 'Erreur 404'*/,
        );
        this.store.dispatch(
          new CoreActions.Go({
            path: ["/location/", this.$paramMap().get("category")],
          }),
        );
        return;
      }

      this.store.dispatch(new CoreActions.SetBreadcrumb(this.$product()));
      this.seo.setTitle(
        this.$product().label.toUpperCase() +
          " en location (44) | " +
          environment.title,
      );
      this.seo.setDescription(this.$product().description);

      this.seo.setMeta({ property: "og:type", content: "og:product" });
      this.seo.setMeta({
        property: "og:title",
        content: this.$product().label,
      });
      this.seo.setMeta({
        property: "og:image",
        content: environment.apiUrl + this.$images()?.[0]?.src + ".webp",
      });
      this.seo.setMeta({
        property: "og:description",
        content: this.$product().description,
      });
      this.seo.setMeta({
        property: "product:price:amount",
        content: (this.$product().price * 1.2).toString(),
      });
      this.seo.setMeta({
        property: "product:price:currency",
        content: "EUR",
      });
    });
  }

  public setMode(label: "ttc" | "ht") {
    this.$mode.set(label);
  }

  public getVariant(label: string) {
    return label
      .replace(this.$product()?.label + " ", "")
      .replace("(", "")
      .replace(")", "");
  }

  protected isNaN(value: number) {
    return isNaN(value);
  }
}
